import React, { useEffect, useState } from "react";
import {
  Table,
  Card,
  Button,
  Space,
  Input,
  Modal,
  Form,
  Row,
  Col,
  Tag,
  Tooltip,
  Select,
  Avatar,
  Popconfirm,
  Statistic,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  PhoneOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  fetchPatientsStart,
  createPatientStart,
  updatePatientStart,
  deletePatientStart,
  clearError,
} from "../../store/slices/billingSlice";
import { Patient, CreatePatientRequest } from "../../types/Billing";
import PageHeader from "../../components/PageHeader";
import dayjs from "dayjs";

const { TextArea } = Input;
const { Option } = Select;

const Patients: React.FC = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { patients, loading, error } = useSelector(
    (state: RootState) => state.billing
  );

  const [searchText, setSearchText] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPatient, setEditingPatient] = useState<Patient | null>(null);
  const [filteredPatients, setFilteredPatients] = useState<Patient[]>([]);

  useEffect(() => {
    dispatch(fetchPatientsStart());
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    const filtered = patients.filter(
      (patient) =>
        patient.name.toLowerCase().includes(searchText.toLowerCase()) ||
        patient.phone.includes(searchText) ||
        (patient.address &&
          patient.address.toLowerCase().includes(searchText.toLowerCase()))
    );
    setFilteredPatients(filtered);
  }, [patients, searchText]);

  const handleAddPatient = () => {
    setEditingPatient(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEditPatient = (patient: Patient) => {
    setEditingPatient(patient);
    form.setFieldsValue({
      name: patient.name,
      age: patient.age,
      gender: patient.gender,
      phone: patient.phone,
      address: patient.address,
    });
    setIsModalVisible(true);
  };

  const handleDeletePatient = (id: string) => {
    dispatch(deletePatientStart(id));
  };

  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      const patientData: CreatePatientRequest = {
        name: values.name,
        age: parseInt(values.age),
        gender: values.gender,
        phone: values.phone,
        address: values.address || "",
      };

      if (editingPatient) {
        dispatch(updatePatientStart({ id: editingPatient._id, patientData }));
      } else {
        dispatch(createPatientStart(patientData));
      }

      setIsModalVisible(false);
      form.resetFields();
      setEditingPatient(null);
    } catch (error) {
      console.error("Form validation failed:", error);
    }
  };

  const columns = [
    {
      title: "Patient ID",
      dataIndex: "patientId",
      key: "patientId",
      render: (patientId: string) => <Tag color="blue">{patientId}</Tag>,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name: string) => (
        <Space>
          <Avatar icon={<UserOutlined />} size="small" />
          <strong>{name}</strong>
        </Space>
      ),
    },
    {
      title: "Age/Gender",
      key: "ageGender",
      render: (record: Patient) => (
        <div>
          <div>{record.age} years</div>
          <Tag
            color={
              record.gender === "male"
                ? "blue"
                : record.gender === "female"
                ? "pink"
                : "default"
            }
          >
            {record.gender ? record.gender.charAt(0).toUpperCase() + record.gender.slice(1) : "N/A"}
          </Tag>
        </div>
      ),
    },
    {
      title: "Contact",
      key: "contact",
      render: (record: Patient) => (
        <div>
          <div>
            <PhoneOutlined /> {record.phone}
          </div>
        </div>
      ),
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      render: (address: string) => (
        <Tooltip title={address}>
          <div
            style={{
              maxWidth: "200px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {address || "Not provided"}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: string) => (
        <div>
          <CalendarOutlined /> {dayjs(date).format("DD/MM/YYYY")}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (record: Patient) => (
        <Space>
          <Tooltip title="Edit Patient">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditPatient(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete Patient"
            description="Are you sure you want to delete this patient?"
            onConfirm={() => handleDeletePatient(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete Patient">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const getGenderStats = () => {
    const genderCounts = patients.reduce((acc, patient) => {
      acc[patient.gender] = (acc[patient.gender] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return [
      { label: "Male", count: genderCounts["male"] || 0, color: "#1890ff" },
      { label: "Female", count: genderCounts["female"] || 0, color: "#f759ab" },
      { label: "Other", count: genderCounts["other"] || 0, color: "#52c41a" },
    ];
  };

  const getAgeGroups = () => {
    const ageGroups = {
      "0-18": 0,
      "19-35": 0,
      "36-60": 0,
      "60+": 0,
    };

    patients.forEach((patient) => {
      if (patient.age <= 18) ageGroups["0-18"]++;
      else if (patient.age <= 35) ageGroups["19-35"]++;
      else if (patient.age <= 60) ageGroups["36-60"]++;
      else ageGroups["60+"]++;
    });

    return Object.entries(ageGroups).map(([range, count]) => ({
      range,
      count,
    }));
  };

  return (
    <div>
      {/* Statistics Cards */}
      <Row gutter={8} style={{ marginBottom: 10 }}>
        <Col span={6}>
          <Card className="stat-card" size="small">
            <Statistic
              title="Total Patients"
              value={patients.length}
              prefix={<UserOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stat-card" size="small">
            <Statistic
              title="Male Patients"
              value={
                getGenderStats().find((g) => g.label === "Male")?.count || 0
              }
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stat-card" size="small">
            <Statistic
              title="Female Patients"
              value={
                getGenderStats().find((g) => g.label === "Female")?.count || 0
              }
              valueStyle={{ color: "#f759ab" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stat-card" size="small">
            <Statistic
              title="Avg Age"
              value={
                patients.length > 0
                  ? Math.round(
                      patients.reduce((sum, p) => sum + p.age, 0) /
                        patients.length
                    )
                  : 0
              }
              suffix="years"
              valueStyle={{ color: "#722ed1" }}
            />
          </Card>
        </Col>
      </Row>

      <PageHeader
        title="Patient Management"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddPatient}
          >
            Add New Patient
          </Button>
        }
      />

      <div className="page-panel">
        {/* Search */}
        <Row gutter={8} style={{ marginBottom: 10 }}>
          <Col span={8}>
            <Input
              placeholder="Search patients by name, phone, or address"
              prefix={<SearchOutlined />}
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Col>
        </Row>

        {/* Table */}
        <Table
          className="dense-table"
          size="small"
          columns={columns}
          dataSource={filteredPatients}
          rowKey="_id"
          loading={loading}
          scroll={{ x: 800 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} patients`,
          }}
        />
      </div>

      {/* Add/Edit Patient Modal */}
      <Modal
        title={editingPatient ? "Edit Patient" : "Add New Patient"}
        open={isModalVisible}
        onOk={handleFormSubmit}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          setEditingPatient(null);
        }}
        okText={editingPatient ? "Update" : "Create"}
        width={600}
      >
        <Form form={form} layout="vertical" requiredMark="optional">
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Full Name"
                rules={[
                  { required: true, message: "Please enter patient name" },
                  { min: 2, message: "Name must be at least 2 characters" },
                ]}
              >
                <Input placeholder="Enter patient name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="age"
                label="Age"
                rules={[
                  { required: true, message: "Please enter patient age" },
                  {
                    validator: (_, value) => {
                      if (value && (value < 0 || value > 150)) {
                        return Promise.reject(
                          new Error("Age must be between 0 and 150")
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  type="number"
                  placeholder="Enter age"
                  min={0}
                  max={150}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={8}>
            <Col span={12}>
              <Form.Item
                name="gender"
                label="Gender"
                rules={[{ required: true, message: "Please select gender" }]}
              >
                <Select placeholder="Select gender">
                  <Option value="male">Male</Option>
                  <Option value="female">Female</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Phone Number"
                rules={[
                  { required: true, message: "Please enter phone number" },
                  {
                    pattern: /^[+]?[\d\s\-\(\)]{10,}$/,
                    message: "Please enter a valid phone number",
                  },
                ]}
              >
                <Input placeholder="Enter phone number" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="address"
            label="Address"
            rules={[
              { min: 5, message: "Address must be at least 5 characters" },
            ]}
          >
            <TextArea
              placeholder="Enter patient address"
              rows={3}
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Patients;
